import ClientEffectiveSettingsCalendar from '@/components/clients/client-effective-sett-calendar'
import NewBadge from '@/components/custom-ui/new-badge'
import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { TagInputMyCustomComp } from '@/components/ui/tag-input-my-custom-comp'
import { envWeb } from '@/lib/env-web'
import { calculateEndTime } from '@/lib/time'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import {
  approvedLocationsSettingsSchema,
  attendanceSettingsSchema,
  breakIntervalsSettingsSchema,
  breakSettingsSchema,
  ClientSettings,
  hoursCalculationSchema,
  regionalHolidayMultipliersSchema,
  regularDayMultipliersSchema,
  regularHolidayMultipliersSchema,
  restDayMultipliersSchema,
  restDaysSettingsSchema,
  SettingKey,
  SettingValueMap,
  shiftSettingsSchema,
  specialHolidayMultipliersSchema,
  tardinessSettingsSchema,
} from '@repo/db/schema'
import { getTypedClientSettingValue } from '@repo/shared_utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import {
  Bell,
  Building2Icon,
  Calculator,
  CalendarDays,
  ChevronLeft,
  Clock,
  Coffee,
  DollarSign,
  Info,
  Loader2,
  MapPin,
  MapPinIcon,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/clients/$clientId')({
  component: RouteComponent,
})

// Email validation function
function validateEmail(email: string): string | null {
  const trimmedEmail = email.trim()

  // Check for empty email
  if (trimmedEmail === '') {
    return null // Empty emails are filtered out, so no error needed
  }

  // Check for spaces
  if (trimmedEmail.includes(' ')) {
    return 'Email cannot contain spaces'
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(trimmedEmail)) {
    return 'Invalid email format'
  }

  return null
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const { clientId } = Route.useParams()

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [isDebouncingRefetch, setIsDebouncingRefetch] = useState(false)
  const [isLocationsDialogOpen, setIsLocationsDialogOpen] = useState(false)
  const [isAddLocationDialogOpen, setIsAddLocationDialogOpen] = useState(false)
  const [editingLocationIndex, setEditingLocationIndex] = useState<number | null>(null)
  const [deletingLocationIndex, setDeletingLocationIndex] = useState<number | null>(null)
  const [newLocationLat, setNewLocationLat] = useState<string>('')
  const [newLocationLong, setNewLocationLong] = useState<string>('')
  const [newLocationName, setNewLocationName] = useState<string>('')
  const [newLocationRadius, setNewLocationRadius] = useState<string>('50')
  const [editLocationLat, setEditLocationLat] = useState<string>('')
  const [editLocationLong, setEditLocationLong] = useState<string>('')
  const [editLocationName, setEditLocationName] = useState<string>('')
  const [editLocationRadius, setEditLocationRadius] = useState<string>('')

  // Notification preferences state
  interface NotificationSlot {
    notificationTime: string
    timeRangeStart: string
    timeRangeEnd: string
  }
  const [notificationEnabled, setNotificationEnabled] = useState(false)
  const [notificationSlots, setNotificationSlots] = useState<NotificationSlot[]>([
    { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
  ])
  const [advanceMinutes, setAdvanceMinutes] = useState(15)
  const [notificationMethods, setNotificationMethods] = useState<Array<'email' | 'sms' | 'both'>>([
    'email',
  ])
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  const [recipientPhoneNumbers, setRecipientPhoneNumbers] = useState<string[]>([])
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null)

  // Fetch notification preferences
  const { data: notificationPrefs, refetch: refetchNotificationPrefs } = useQuery({
    ...api.clientSettings.getNotificationPreferences.queryOptions({
      clientId,
    }),
  })

  // Update notification preferences when data loads
  useEffect(() => {
    if (notificationPrefs) {
      setNotificationEnabled(notificationPrefs.enabled)
      // Handle both old format (single notificationTime) and new format (notificationSlots)
      if (notificationPrefs.notificationSlots && notificationPrefs.notificationSlots.length > 0) {
        setNotificationSlots(notificationPrefs.notificationSlots)
      } else {
        // Default slot if no slots exist
        setNotificationSlots([
          { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
        ])
      }
      setAdvanceMinutes(notificationPrefs.advanceMinutes)
      setNotificationMethods(notificationPrefs.methods)
      setRecipientEmails(
        notificationPrefs.recipientEmails.length > 0 ? notificationPrefs.recipientEmails : [],
      )
      setRecipientPhoneNumbers(
        notificationPrefs.recipientPhoneNumbers.length > 0
          ? notificationPrefs.recipientPhoneNumbers
          : [],
      )
      setEmailValidationError(null)
    } else {
      // Reset to defaults if no preferences exist
      setNotificationEnabled(false)
      setNotificationSlots([
        { notificationTime: '09:00', timeRangeStart: '06:00', timeRangeEnd: '09:00' },
      ])
      setAdvanceMinutes(15)
      setNotificationMethods(['email'])
      setRecipientEmails([])
      setRecipientPhoneNumbers([])
      setEmailValidationError(null)
    }
  }, [notificationPrefs])

  const updateNotificationPreferencesMutation = useMutation({
    ...api.clientSettings.updateNotificationPreferences.mutationOptions({
      onSuccess: () => {
        toast.success('Notification preferences saved successfully')
        refetchNotificationPrefs()
      },
      onError: (error) => {
        toast.error(`Failed to save notification preferences: ${error.message}`)
      },
    }),
  })

  // Fetch client settings for the given clientId
  const { data: settings, isPending: isPendingClientSettings } = useQuery({
    ...api.clientSettings.getClientSettings.queryOptions({
      clientId,
    }),
  })

  // Query for client settings for a specific date when selectedDate is set
  const clientSettingsForDateQuery = useQuery({
    ...api.clientSettings.getClientSettingsForDate.queryOptions({
      clientId,
      date: selectedDate,
    }),
    // manual; only run when user presses refetch
    enabled: false,
  })
  console.log({ clientSettingsForDateQueryData: clientSettingsForDateQuery.data })

  const { data: client, isPending: isPendingClient } = useQuery({
    ...api.client.getClientById.queryOptions({
      clientId,
    }),
  })

  const { data: approvedLocations } = useQuery({
    ...api.clientSettings.getClientApprovedLocations.queryOptions({
      clientId,
    }),
    enabled: isLocationsDialogOpen,
  })

  // Helper function to get effective setting value based on date selection
  function getEffectiveClientSettingValue<K extends SettingKey>(
    key: K,
  ): SettingValueMap[K]['infer'] | undefined {
    // If a date is selected and we have date-specific data, use that
    if (selectedDate && clientSettingsForDateQuery.data) {
      return getTypedClientSettingValue(clientSettingsForDateQuery.data, key)
    }

    // Otherwise use the default settings
    return getTypedClientSettingValue(settings, key)
  }

  /**
   * Get all allowance_settings entries active for a specific date.
   * For allowance_settings, multiple entries can be active simultaneously (overlapping ranges).
   */
  function getAllActiveClientAllowanceSettings(
    settings: ClientSettings[] | undefined,
    date: Date | undefined,
  ): Array<
    ClientSettings & {
      value: {
        frequency: 'daily' | 'monthly'
        amount: number
        name: string
        isTaxable?: boolean
        nonTaxableCap?: number
      }
    }
  > {
    if (!settings) return []

    const targetDate = date ?? new Date()
    const manilaDate = DateTime.fromJSDate(targetDate).setZone('Asia/Manila').startOf('day')

    return settings
      .filter((setting) => {
        if (setting.key !== 'allowance_settings') return false

        const validFromManila = DateTime.fromJSDate(setting.validFrom)
          .setZone('Asia/Manila')
          .startOf('day')
        const validToManila = setting.validTo
          ? DateTime.fromJSDate(setting.validTo).setZone('Asia/Manila').startOf('day')
          : null

        const isAfterOrOnValidFrom = manilaDate >= validFromManila
        const isBeforeOrOnValidTo = validToManila ? manilaDate <= validToManila : true

        return isAfterOrOnValidFrom && isBeforeOrOnValidTo
      })
      .filter(
        (
          setting,
        ): setting is ClientSettings & {
          value: {
            frequency: 'daily' | 'monthly'
            amount: number
            name: string
            isTaxable?: boolean
            nonTaxableCap?: number
          }
        } => {
          return (
            typeof setting.value === 'object' &&
            setting.value !== null &&
            'frequency' in setting.value &&
            'amount' in setting.value &&
            'name' in setting.value
          )
        },
      )
      .sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime())
  }

  const officeHoursFormDefaultValues = {
    dayShiftStartTime: getTypedClientSettingValue(settings, 'day_shift_start_time') ?? undefined,
    dayShiftEndTime: getTypedClientSettingValue(settings, 'day_shift_end_time') ?? undefined,
    nightShiftStartTime:
      getTypedClientSettingValue(settings, 'night_shift_start_time') ?? undefined,
    nightShiftEndTime: getTypedClientSettingValue(settings, 'night_shift_end_time') ?? undefined,
    employeeShiftHoursStartTime:
      getTypedClientSettingValue(settings, 'employee_shift_hours_start_time') ?? undefined,
    // employeeShiftHoursEndTime:
    //   getTypedClientSettingValue(settings, 'employee_shift_hours_end_time') ?? undefined,
    maxDailyRegHrs: getTypedClientSettingValue(settings, 'max_daily_reg_hrs') ?? undefined,
    maxDailyOtHrs: getTypedClientSettingValue(settings, 'max_daily_ot_hrs') ?? undefined,
    maxDailyNsHrs: getTypedClientSettingValue(settings, 'max_daily_ns_hrs') ?? undefined,
    maxDailyNsOtHrs: getTypedClientSettingValue(settings, 'max_daily_ns_ot_hrs') ?? undefined,
  }

  const shiftHoursForm = useForm<typeof shiftSettingsSchema.infer>({
    resolver: arktypeResolver(shiftSettingsSchema),
    defaultValues: officeHoursFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      shiftHoursForm.reset(officeHoursFormDefaultValues)
    }
  }, [settings])

  const tardinessFormDefaultValues = {
    tardinessGracePeriodMinutes:
      getTypedClientSettingValue(settings, 'tardiness_grace_period') ?? undefined,
    enforceTardinessIncompleteHours:
      getTypedClientSettingValue(settings, 'enforce_tardiness_incomplete_hours') ?? undefined,
  }

  const tardinessForm = useForm<typeof tardinessSettingsSchema.infer>({
    resolver: arktypeResolver(tardinessSettingsSchema),
    defaultValues: tardinessFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      tardinessForm.reset(tardinessFormDefaultValues)
    }
  }, [settings])

  const restDaysFormDefaultValues = {
    restDays: getTypedClientSettingValue(settings, 'rest_days') ?? undefined,
  }

  const restDaysForm = useForm<typeof restDaysSettingsSchema.infer>({
    resolver: arktypeResolver(restDaysSettingsSchema),
    defaultValues: restDaysFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      restDaysForm.reset(restDaysFormDefaultValues)
    }
  }, [settings])

  const attendanceFormDefaultValues = {
    minimumHrsForPresent: getTypedClientSettingValue(settings, 'min_hrs_for_present') ?? undefined,
  }

  const attendanceForm = useForm<typeof attendanceSettingsSchema.infer>({
    resolver: arktypeResolver(attendanceSettingsSchema),
    defaultValues: attendanceFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      attendanceForm.reset(attendanceFormDefaultValues)
    }
  }, [settings])

  const breakFormDefaultValues = {
    breakTimeDayMins: getTypedClientSettingValue(settings, 'break_time_day_mins') ?? undefined,
    breakTimeNightMins: getTypedClientSettingValue(settings, 'break_time_night_mins') ?? undefined,
    isPaidBreak: getTypedClientSettingValue(settings, 'is_paid_break') ?? undefined,
  }

  const breakForm = useForm<typeof breakSettingsSchema.infer>({
    resolver: arktypeResolver(breakSettingsSchema),
    defaultValues: breakFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      breakForm.reset(breakFormDefaultValues)
    }
  }, [settings])

  // Break Intervals (new)
  const breakIntervalsFormDefaultValues = {
    breakIntervals:
      (getTypedClientSettingValue(settings, 'break_intervals') as string[] | undefined) ??
      undefined,
  }

  const breakIntervalsForm = useForm<typeof breakIntervalsSettingsSchema.infer>({
    resolver: arktypeResolver(breakIntervalsSettingsSchema),
    defaultValues: breakIntervalsFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      breakIntervalsForm.reset(breakIntervalsFormDefaultValues)
    }
  }, [settings])

  const hoursCalculationFormDefaultValues = {
    useEmployeeShiftSettings: Boolean(
      getTypedClientSettingValue(settings, 'use_employee_shift_settings'),
    ),
    useEarlyOvertime: Boolean(getTypedClientSettingValue(settings, 'use_early_overtime')),
  }

  const hoursCalculationForm = useForm<typeof hoursCalculationSchema.infer>({
    resolver: arktypeResolver(hoursCalculationSchema),
    defaultValues: hoursCalculationFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      hoursCalculationForm.reset({
        useEmployeeShiftSettings: Boolean(
          getTypedClientSettingValue(settings, 'use_employee_shift_settings'),
        ),
        useEarlyOvertime: Boolean(getTypedClientSettingValue(settings, 'use_early_overtime')),
      })
    }
  }, [settings])

  // regular day multipliers
  const regularDayMultipliersFormDefaultValues = {
    regularHoursMultiplier:
      getTypedClientSettingValue(settings, 'regular_hours_multiplier') ?? undefined,
    overtimeMultiplier: getTypedClientSettingValue(settings, 'overtime_multiplier') ?? undefined,
    nightDifferentialMultiplier:
      getTypedClientSettingValue(settings, 'night_differential_multiplier') ?? undefined,
    otNdHoursMultiplier:
      getTypedClientSettingValue(settings, 'ot_nd_hours_multiplier') ?? undefined,
  }

  const regularDayMultipliersForm = useForm<typeof regularDayMultipliersSchema.infer>({
    resolver: arktypeResolver(regularDayMultipliersSchema),
    defaultValues: regularDayMultipliersFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      regularDayMultipliersForm.reset(regularDayMultipliersFormDefaultValues)
    }
  }, [settings])

  // rest day multipliers

  const restDayMultipliersFormDefaultValues = {
    restDayMultiplier: getTypedClientSettingValue(settings, 'rest_day_multiplier') ?? undefined,
    restDayNdMultiplier:
      getTypedClientSettingValue(settings, 'rest_day_nd_multiplier') ?? undefined,
    restDayOtMultiplier:
      getTypedClientSettingValue(settings, 'rest_day_ot_multiplier') ?? undefined,
    restDayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'rest_day_ot_nd_multiplier') ?? undefined,
  }

  const restDayMultipliersForm = useForm<typeof restDayMultipliersSchema.infer>({
    resolver: arktypeResolver(restDayMultipliersSchema),
    defaultValues: restDayMultipliersFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      restDayMultipliersForm.reset(restDayMultipliersFormDefaultValues)
    }
  }, [settings])

  // regular holiday multipliers

  const regularHolidayMultipliersFormDefaultValues = {
    regularHolidayMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_multiplier') ?? undefined,
    regularHolidayNdMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_nd_multiplier') ?? undefined,
    regularHolidayOtMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_ot_multiplier') ?? undefined,
    regularHolidayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_ot_nd_multiplier') ?? undefined,
    regularHolidayRestDayMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_rest_day_multiplier') ?? undefined,
    regularHolidayRestDayNdMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_rest_day_nd_multiplier') ?? undefined,
    regularHolidayRestDayOtMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_rest_day_ot_multiplier') ?? undefined,
    regularHolidayRestDayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'regular_holiday_rest_day_ot_nd_multiplier') ??
      undefined,
  }

  const regularHolidayMultipliersForm = useForm<typeof regularHolidayMultipliersSchema.infer>({
    resolver: arktypeResolver(regularHolidayMultipliersSchema),
    defaultValues: regularHolidayMultipliersFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      regularHolidayMultipliersForm.reset(regularHolidayMultipliersFormDefaultValues)
    }
  }, [settings])

  // special holiday multipliers

  const specialHolidayMultipliersFormDefaultValues = {
    specialHolidayMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_multiplier') ?? undefined,
    specialHolidayNdMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_nd_multiplier') ?? undefined,
    specialHolidayOtMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_ot_multiplier') ?? undefined,
    specialHolidayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_ot_nd_multiplier') ?? undefined,
    specialHolidayRestDayMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_rest_day_multiplier') ?? undefined,
    specialHolidayRestDayNdMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_rest_day_nd_multiplier') ?? undefined,
    specialHolidayRestDayOtMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_rest_day_ot_multiplier') ?? undefined,
    specialHolidayRestDayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'special_holiday_rest_day_ot_nd_multiplier') ??
      undefined,
  }

  const specialHolidayMultipliersForm = useForm<typeof specialHolidayMultipliersSchema.infer>({
    resolver: arktypeResolver(specialHolidayMultipliersSchema),
    defaultValues: specialHolidayMultipliersFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      specialHolidayMultipliersForm.reset(specialHolidayMultipliersFormDefaultValues)
    }
  }, [settings])

  // regional holiday multipliers

  const regionalHolidayMultipliersFormDefaultValues = {
    regionalHolidayMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_multiplier') ?? undefined,
    regionalHolidayNdMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_nd_multiplier') ?? undefined,
    regionalHolidayOtMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_ot_multiplier') ?? undefined,
    regionalHolidayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_ot_nd_multiplier') ?? undefined,
    regionalHolidayRestDayMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_rest_day_multiplier') ?? undefined,
    regionalHolidayRestDayNdMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_rest_day_nd_multiplier') ?? undefined,
    regionalHolidayRestDayOtMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_rest_day_ot_multiplier') ?? undefined,
    regionalHolidayRestDayOtNdMultiplier:
      getTypedClientSettingValue(settings, 'regional_holiday_rest_day_ot_nd_multiplier') ??
      undefined,
  }

  const regionalHolidayMultipliersForm = useForm<typeof regionalHolidayMultipliersSchema.infer>({
    resolver: arktypeResolver(regionalHolidayMultipliersSchema),
    defaultValues: regionalHolidayMultipliersFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      regionalHolidayMultipliersForm.reset(regionalHolidayMultipliersFormDefaultValues)
    }
  }, [settings])

  // Approved Locations Form
  const approvedLocationsFormDefaultValues = {
    approvedLocations: (getTypedClientSettingValue(settings, 'approved_locations') ??
      []) as Array<{
      lat: number
      long: number
      name: string
      radiusMeters?: number
    }>,
  }

  const approvedLocationsForm = useForm<typeof approvedLocationsSettingsSchema.infer>({
    resolver: arktypeResolver(approvedLocationsSettingsSchema),
    defaultValues: approvedLocationsFormDefaultValues,
  })

  useEffect(() => {
    if (settings) {
      approvedLocationsForm.reset(approvedLocationsFormDefaultValues)
    }
  }, [settings])

  // Mutations for approved locations
  const createSettingMutation = useMutation({
    ...api.clientSettings.createSetting.mutationOptions({
      onSuccess: (data) => {
        toast.success(data.message)
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientSettings.queryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientApprovedLocations.queryKey({
            clientId,
          }),
        })
        if (selectedDate) {
          clientSettingsForDateQuery.refetch()
        }
      },
      onError: (error) => {
        console.error('Error creating setting:', error)
        toast.error('Failed to create setting', {
          description: error.message,
          action: {
            label: 'Close',
            onClick: () => toast.dismiss(),
          },
        })
      },
    }),
  })

  const updateSettingMutation = useMutation({
    ...api.clientSettings.updateSetting.mutationOptions({
      onSuccess: (data) => {
        toast.success(data.message)
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientSettings.queryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientApprovedLocations.queryKey({
            clientId,
          }),
        })
        if (selectedDate) {
          clientSettingsForDateQuery.refetch()
        }
      },
      onError: (error) => {
        console.error('Error updating setting:', error)
        toast.error('Failed to update setting', {
          description: error.message,
          action: {
            label: 'Close',
            onClick: () => toast.dismiss(),
          },
        })
      },
    }),
  })

  const deleteSettingMutation = useMutation({
    ...api.clientSettings.deleteSetting.mutationOptions({
      onSuccess: (data) => {
        toast.success(data.message)
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientSettings.queryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: api.clientSettings.getClientApprovedLocations.queryKey({
            clientId,
          }),
        })
        if (selectedDate) {
          clientSettingsForDateQuery.refetch()
        }
      },
      onError: (error) => {
        console.error('Error deleting setting:', error)
        toast.error('Failed to delete setting', {
          description: error.message,
          action: {
            label: 'Close',
            onClick: () => toast.dismiss(),
          },
        })
      },
    }),
  })

  if (isPendingClient || isPendingClientSettings) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Link to="/dashboard/admin/clients">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="mr-1" />
              Back
            </Button>
          </Link>
        </div>
        <DashboardHeader
          heading="Client Settings"
          text="Configure this client's time and attendance policies."
        />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )
  }

  if (!client) {
    return (
      <DashboardShell>
        <div className="mb-2">
          <Link to="/dashboard/admin/clients">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="mr-1" />
              Back
            </Button>
          </Link>
        </div>
        <DashboardHeader
          heading="Client Settings"
          text="Configure this client's time and attendance policies."
        />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="h-40 w-full">Client not found</div>
        </div>
      </DashboardShell>
    )
  }

  return (
    <DashboardShell>
      <div className="mb-2">
        <Link to="/dashboard/admin/clients">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="mr-1" />
            Back
          </Button>
        </Link>
      </div>
      <DashboardHeader
        heading="Client Settings"
        text="Configure this client's time and attendance policies."
      />

      <div className="mb-6 flex items-center gap-x-4 p-4 bg-gradient-to-r from-green-200/80 to-transparent rounded-lg dark:from-green-650/20">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Building2Icon className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">{client.name}</h2>
          <div className="flex items-center gap-x-2 text-sm text-muted-foreground">
            <MapPinIcon className="h-4 w-4" />
            <p className="font-medium">{client.location}</p>
          </div>
        </div>
      </div>

      {/* Date Picker for Viewing Client Settings */}
      <div className="mb-6 flex items-center justify-end">
        <Popover>
          <PopoverTrigger asChild>
            <div className="relative inline-block min-w-[240px]">
              <Button
                variant="default"
                className="w-full justify-start bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-all duration-200"
              >
                <Clock className="mr-3 h-5 w-5" />
                {selectedDate ? (
                  DateTime.fromJSDate(selectedDate).toLocaleString(DateTime.DATE_MED)
                ) : (
                  <span className="opacity-80">View Client Settings for a specific date</span>
                )}
              </Button>
              {/* <span className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 bg-fuchsia-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-md select-none">
                New
              </span> */}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="end">
            <div className="space-y-3">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => setSelectedDate(d)}
                initialFocus
              />
              <Button
                onClick={async () => {
                  if (!clientId || !selectedDate) return
                  setIsDebouncingRefetch(true)
                  // small delay to avoid noisy refetches
                  setTimeout(async () => {
                    await clientSettingsForDateQuery.refetch()
                    setIsDebouncingRefetch(false)
                  }, 400)
                }}
                disabled={
                  !selectedDate || clientSettingsForDateQuery.isFetching || isDebouncingRefetch
                }
              >
                {(clientSettingsForDateQuery.isFetching || isDebouncingRefetch) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Refetch settings
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Shift Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Shift Settings</CardTitle>
            </div>
            <CardDescription>Update multiple shift-related settings at once</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...shiftHoursForm}>
              <form className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold mb-3">Day Shift Times</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={shiftHoursForm.control}
                      name="dayShiftStartTime"
                      render={() => {
                        const effectiveValue =
                          getEffectiveClientSettingValue('day_shift_start_time')
                        return (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <div className="flex items-center justify-between">
                                <Input
                                  placeholder=""
                                  type="time"
                                  value={effectiveValue || ''}
                                  disabled
                                />
                                {settings && (
                                  <>
                                    <ClientEffectiveSettingsCalendar
                                      clientId={clientId}
                                      clientName={client?.name}
                                      settingKey="day_shift_start_time"
                                      settingDisplayName="Day Shift Start Time"
                                      allSettings={settings}
                                      onCreateOrUpdateSetting={() => {
                                        queryClient.invalidateQueries({
                                          queryKey:
                                            api.clientSettings.getClientSettings.queryKey(),
                                        })
                                        if (selectedDate) {
                                          clientSettingsForDateQuery.refetch()
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                    <FormField
                      control={shiftHoursForm.control}
                      name="dayShiftEndTime"
                      render={() => {
                        const effectiveValue = getEffectiveClientSettingValue('day_shift_end_time')
                        return (
                          <FormItem>
                            <FormLabel>End Time</FormLabel>
                            <FormControl>
                              <div className="flex items-center justify-between">
                                <Input
                                  placeholder=""
                                  type="time"
                                  value={effectiveValue || ''}
                                  disabled
                                />
                                {settings && (
                                  <>
                                    <ClientEffectiveSettingsCalendar
                                      clientId={clientId}
                                      clientName={client?.name}
                                      settingKey="day_shift_end_time"
                                      settingDisplayName="Day Shift End Time"
                                      allSettings={settings}
                                      onCreateOrUpdateSetting={() => {
                                        queryClient.invalidateQueries({
                                          queryKey:
                                            api.clientSettings.getClientSettings.queryKey(),
                                        })
                                        if (selectedDate) {
                                          clientSettingsForDateQuery.refetch()
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-3">Night Shift Times</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={shiftHoursForm.control}
                      name="nightShiftStartTime"
                      render={() => {
                        const effectiveValue =
                          getEffectiveClientSettingValue('night_shift_start_time')
                        return (
                          <FormItem>
                            <FormLabel>Start Time</FormLabel>
                            <FormControl>
                              <div className="flex items-center justify-between">
                                <Input
                                  placeholder=""
                                  type="time"
                                  value={effectiveValue || ''}
                                  disabled
                                />
                                {settings && (
                                  <>
                                    <ClientEffectiveSettingsCalendar
                                      clientId={clientId}
                                      clientName={client?.name}
                                      settingKey="night_shift_start_time"
                                      settingDisplayName="Night Shift Start Time"
                                      allSettings={settings}
                                      onCreateOrUpdateSetting={() => {
                                        queryClient.invalidateQueries({
                                          queryKey:
                                            api.clientSettings.getClientSettings.queryKey(),
                                        })
                                        if (selectedDate) {
                                          clientSettingsForDateQuery.refetch()
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                    <FormField
                      control={shiftHoursForm.control}
                      name="nightShiftEndTime"
                      render={() => {
                        const effectiveValue =
                          getEffectiveClientSettingValue('night_shift_end_time')
                        return (
                          <FormItem>
                            <FormLabel>End Time</FormLabel>
                            <FormControl>
                              <div className="flex items-center justify-between">
                                <Input
                                  placeholder=""
                                  type="time"
                                  value={effectiveValue || ''}
                                  disabled
                                />
                                {settings && (
                                  <>
                                    <ClientEffectiveSettingsCalendar
                                      clientId={clientId}
                                      clientName={client?.name}
                                      settingKey="night_shift_end_time"
                                      settingDisplayName="Night Shift End Time"
                                      allSettings={settings}
                                      onCreateOrUpdateSetting={() => {
                                        queryClient.invalidateQueries({
                                          queryKey:
                                            api.clientSettings.getClientSettings.queryKey(),
                                        })
                                        if (selectedDate) {
                                          clientSettingsForDateQuery.refetch()
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </div>
                </div>
                <Separator className="my-4" />
                <h3 className="text-sm font-semibold mb-3">Employee Shift Times</h3>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={shiftHoursForm.control}
                    name="employeeShiftHoursStartTime"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'employee_shift_hours_start_time',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Start Time</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder=""
                                type="time"
                                value={effectiveValue || ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="employee_shift_hours_start_time"
                                    settingDisplayName="Employee Shift Hours Start Time"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <div className="flex items-center justify-between">
                        <Input
                          placeholder=""
                          readOnly
                          type="time"
                          value={calculateEndTime(
                            getEffectiveClientSettingValue('employee_shift_hours_start_time') ??
                              '',
                            getEffectiveClientSettingValue('max_daily_reg_hrs') ?? 0,
                            0,
                          )}
                          disabled
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
                <Separator className="my-4" />
                <h3 className="text-sm font-semibold mb-3">Max Daily Hours</h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyRegHrs"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('max_daily_reg_hrs')
                      return (
                        <FormItem>
                          <FormLabel>Reg Hours</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder=""
                                type="number"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="max_daily_reg_hrs"
                                    settingDisplayName="Max Daily Regular Hours"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                    // disabled
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyOtHrs"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('max_daily_ot_hrs')
                      return (
                        <FormItem>
                          <FormLabel>OT Hours</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder=""
                                type="number"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="max_daily_ot_hrs"
                                    settingDisplayName="Max Daily OT Hours"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyNsHrs"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('max_daily_ns_hrs')
                      return (
                        <FormItem>
                          <FormLabel>NS Hours</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder=""
                                type="number"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="max_daily_ns_hrs"
                                    settingDisplayName="Max Daily NS Hours"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={shiftHoursForm.control}
                    name="maxDailyNsOtHrs"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('max_daily_ns_ot_hrs')
                      return (
                        <FormItem>
                          <FormLabel>NS OT Hours</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                placeholder=""
                                type="number"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="max_daily_ns_ot_hrs"
                                    settingDisplayName="Max Daily NS OT Hours"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Hours Calculation Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Hours Calculation Settings</CardTitle>
            </div>
            <CardDescription>Configure how hours are calculated for timesheets</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...hoursCalculationForm}>
              <form className="space-y-4">
                <FormField
                  control={hoursCalculationForm.control}
                  name="useEmployeeShiftSettings"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue(
                      'use_employee_shift_settings',
                    )
                    return (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Switch checked={effectiveValue} disabled />
                          <FormLabel className="mb-0">Use Employee Shift Settings</FormLabel>
                          {settings && (
                            <>
                              <ClientEffectiveSettingsCalendar
                                clientId={clientId}
                                clientName={client?.name}
                                settingKey="use_employee_shift_settings"
                                settingDisplayName="Use Employee Shift Settings"
                                allSettings={settings}
                                onCreateOrUpdateSetting={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: api.clientSettings.getClientSettings.queryKey(),
                                  })
                                  if (selectedDate) {
                                    clientSettingsForDateQuery.refetch()
                                  }
                                }}
                                disabled={true}
                              />
                            </>
                          )}
                        </div>
                        <FormMessage />
                        <p className="text-muted-foreground text-sm">
                          Use scheduled shift start for night differential regular window (if off,
                          use actual clock-in).
                        </p>
                      </FormItem>
                    )
                  }}
                />

                <FormField
                  control={hoursCalculationForm.control}
                  name="useEarlyOvertime"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('use_early_overtime')
                    return (
                      <FormItem>
                        <div className="flex items-center gap-2 mb-2">
                          <Switch checked={effectiveValue} disabled />
                          <FormLabel className="mb-0">Count Early Clock-in as Overtime</FormLabel>
                          {settings && (
                            <>
                              <ClientEffectiveSettingsCalendar
                                clientId={clientId}
                                clientName={client?.name}
                                settingKey="use_early_overtime"
                                settingDisplayName="Count Early Clock-in as Overtime"
                                allSettings={settings}
                                onCreateOrUpdateSetting={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: api.clientSettings.getClientSettings.queryKey(),
                                  })
                                  if (selectedDate) {
                                    clientSettingsForDateQuery.refetch()
                                  }
                                }}
                              />
                            </>
                          )}
                        </div>
                        <FormMessage />
                        <p className="text-muted-foreground text-sm">
                          If enabled, hours worked before the regular shift start time count as
                          overtime
                        </p>
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Tardiness Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Tardiness Settings</CardTitle>
            </div>
            <CardDescription>
              Minimum number of minutes before an employee is considered late
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...tardinessForm}>
              <form className="space-y-6">
                <FormField
                  control={tardinessForm.control}
                  name="tardinessGracePeriodMinutes"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('tardiness_grace_period')
                    return (
                      <FormItem>
                        <div className="flex items-center justify-between mb-2">
                          <FormLabel>Grace Period (minutes)</FormLabel>
                        </div>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={effectiveValue ?? ''}
                              disabled
                            />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="tardiness_grace_period"
                                  settingDisplayName="Tardiness Grace Period"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
                <FormField
                  control={tardinessForm.control}
                  name="enforceTardinessIncompleteHours"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue(
                      'enforce_tardiness_incomplete_hours',
                    )
                    return (
                      <FormItem>
                        <div className="flex items-center justify-between mb-2">
                          <FormLabel>Enforce Tardiness for Incomplete Hours</FormLabel>
                        </div>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Switch checked={Boolean(effectiveValue)} disabled />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="enforce_tardiness_incomplete_hours"
                                  settingDisplayName="Enforce Tardiness for Incomplete Hours"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                        <p className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                          If enabled, tardiness will be calculated even if the clock-in is within
                          the grace period, when the employee has not completed their required
                          hours. <br /> This will also calculate hours after the regular shift end
                          time as part of their regular hours.
                        </p>
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Rest Days (Updated with Weekly Day Types) Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <CardTitle className="text-lg">Rest Days Settings</CardTitle>
            </div>
            <CardDescription>Configure weekly rest days and optional work days</CardDescription>
          </CardHeader>
          <CardContent>
            {(() => {
              // Prefer date-specific effective settings when selected
              const weeklyEffective = selectedDate
                ? getEffectiveClientSettingValue('weekly_day_types')
                : undefined
              const weekly = weeklyEffective as
                | Record<string, 'rest_day' | 'optional_work_day' | undefined>
                | undefined

              return (
                <div>
                  {settings && (
                    <div className="flex items-center gap-2 mb-2">
                      <ClientEffectiveSettingsCalendar
                        clientId={clientId}
                        clientName={client?.name}
                        settingKey="weekly_day_types"
                        settingDisplayName="Rest / Optional Work Days"
                        allSettings={settings}
                        onCreateOrUpdateSetting={() => {
                          queryClient.invalidateQueries({
                            queryKey: api.clientSettings.getClientSettings.queryKey(),
                          })
                          if (selectedDate) {
                            clientSettingsForDateQuery.refetch()
                          }
                        }}
                      />
                    </div>
                  )}

                  <div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-6 mb-2">
                      {[
                        'sunday',
                        'monday',
                        'tuesday',
                        'wednesday',
                        'thursday',
                        'friday',
                        'saturday',
                      ].map((day) => {
                        const weeklyType = weekly?.[day as keyof typeof weekly]
                        const isRestFromWeekly = weeklyType === 'rest_day'
                        const isOptionalFromWeekly = weeklyType === 'optional_work_day'
                        const isAnyMarked = isRestFromWeekly || isOptionalFromWeekly

                        return (
                          <div key={day} className="flex items-center space-x-2">
                            <Checkbox id={day} checked={isAnyMarked} disabled />
                            <Label htmlFor={day} className="capitalize">
                              {day}
                            </Label>
                            {isRestFromWeekly ? (
                              <Badge className="ml-1 h-5 px-1.5 text-[10px] leading-none bg-emerald-100 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                                RD
                              </Badge>
                            ) : null}
                            {isOptionalFromWeekly ? (
                              <Badge className="ml-1 h-5 px-1.5 text-[10px] leading-none bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                                OWD
                              </Badge>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                    <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                      <p>RD: Rest Day</p>
                      <p>OWD: Optional Work Day</p>
                    </div>
                  </div>
                </div>
              )
            })()}
          </CardContent>
        </Card>

        {/* Rest Days Settings */}
        {/* <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <CardTitle className="text-lg line-through">Rest Days (Deprecated)</CardTitle>
            </div>
            <CardDescription>Configure weekly rest days</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...restDaysForm}>
              <form className="space-y-6">
                <FormField
                  control={restDaysForm.control}
                  name="restDays"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('rest_days')
                    return (
                      <FormItem>
                        <div className="flex items-center mb-2">
                          <FormLabel className="mb-0">Rest Days</FormLabel>
                          {settings && (
                            <>
                              <ClientEffectiveSettingsCalendar
                                clientId={clientId}
                                clientName={client?.name}
                                settingKey="rest_days"
                                settingDisplayName="Rest Days"
                                allSettings={settings}
                                onCreateOrUpdateSetting={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: api.clientSettings.getClientSettings.queryKey(),
                                  })
                                  if (selectedDate) {
                                    clientSettingsForDateQuery.refetch()
                                  }
                                }}
                                // disabled={true}
                              />
                            </>
                          )}
                        </div>
                        <FormControl>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-6 mb-4">
                            {[
                              'sunday',
                              'monday',
                              'tuesday',
                              'wednesday',
                              'thursday',
                              'friday',
                              'saturday',
                            ].map((day) => (
                              <div key={day} className="flex items-center space-x-2">
                                <Checkbox
                                  id={day}
                                  checked={effectiveValue?.includes(day)}
                                  disabled
                                />
                                <Label htmlFor={day} className="capitalize">
                                  {day}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card> */}

        {/* Attendance Settings Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Attendance Settings</CardTitle>
            </div>
            <CardDescription>Configure attendance settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...attendanceForm}>
              <form
                // onSubmit={attendanceForm.handleSubmit((values) => {
                // updateAttendanceSettingsMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-6"
              >
                <FormField
                  control={attendanceForm.control}
                  name="minimumHrsForPresent"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('min_hrs_for_present')
                    return (
                      <FormItem>
                        <FormLabel>Minimum Hours for Present</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={effectiveValue ?? ''}
                              disabled
                            />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="min_hrs_for_present"
                                  settingDisplayName="Minimum Hours for Present"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Break Intervals */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Coffee className="h-4 w-4" />
              <CardTitle className="text-lg">Break Intervals</CardTitle>
            </div>
            <CardDescription>Specify one or more break intervals per shift</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...breakIntervalsForm}>
              <form className="space-y-6">
                <FormField
                  control={breakIntervalsForm.control}
                  name="breakIntervals"
                  render={() => {
                    // const effectiveValue = getEffectiveClientSettingValue('break_intervals') as
                    //   | string[]
                    //   | undefined
                    return (
                      <FormItem>
                        <div className="flex items-center mb-2">
                          <FormLabel className="mb-0">Break Intervals</FormLabel>
                          {settings && (
                            <>
                              <ClientEffectiveSettingsCalendar
                                clientId={clientId}
                                clientName={client?.name}
                                settingKey="break_intervals"
                                settingDisplayName="Break Intervals"
                                allSettings={settings}
                                onCreateOrUpdateSetting={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: api.clientSettings.getClientSettings.queryKey(),
                                  })
                                  if (selectedDate) {
                                    clientSettingsForDateQuery.refetch()
                                  }
                                }}
                              />
                            </>
                          )}
                        </div>
                        {/* <FormControl>
                          <div className="space-y-2">
                            {effectiveValue?.map((interval, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Input value={interval} readOnly />
                              </div>
                            ))}
                          </div>
                        </FormControl> */}
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />

                <FormField
                  control={breakForm.control}
                  name="isPaidBreak"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('is_paid_break')
                    return (
                      <FormItem>
                        <FormLabel>Is Paid Break</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Switch checked={effectiveValue} disabled />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="is_paid_break"
                                  settingDisplayName="Is Paid Break"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Approved Locations */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <CardTitle className="text-lg">Approved Locations</CardTitle>
            </div>
            <CardDescription>
              Define approved locations for timesheet validation. Employees must clock in/out
              within the configured radius (default 50 meters) of these locations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...approvedLocationsForm}>
              <form className="space-y-6">
                <FormField
                  control={approvedLocationsForm.control}
                  name="approvedLocations"
                  render={({ field }) => {
                    const visibleLocations = approvedLocations ?? field.value ?? []
                    return (
                      <FormItem>
                        <div className="flex items-center justify-between mb-2">
                          <FormLabel>Approved Locations</FormLabel>
                          {/* {settings && (
                            <div className="flex items-center gap-2">
                              <ClientEffectiveSettingsCalendar
                                clientId={clientId}
                                clientName={client?.name}
                                settingKey="approved_locations"
                                settingDisplayName="Approved Locations"
                                allSettings={settings}
                                onCreateOrUpdateSetting={() => {
                                  queryClient.invalidateQueries({
                                    queryKey: api.clientSettings.getClientSettings.queryKey(),
                                  })
                                  queryClient.invalidateQueries({
                                    queryKey:
                                      api.clientSettings.getClientApprovedLocations.queryKey({
                                        clientId,
                                      }),
                                  })
                                  if (selectedDate) {
                                    clientSettingsForDateQuery.refetch()
                                  }
                                }}
                              />
                            </div>
                          )} */}
                        </div>
                        <div className="mb-4">
                          <Dialog
                            open={isLocationsDialogOpen}
                            onOpenChange={setIsLocationsDialogOpen}
                          >
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5">
                                <MapPin className="h-3 w-3" />
                                View {visibleLocations.length} Location
                                {visibleLocations.length === 1 ? '' : 's'}
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-[600px] max-h-[80vh] flex flex-col">
                              <DialogHeader>
                                <DialogTitle>Approved Locations</DialogTitle>
                              </DialogHeader>
                              <div className="flex items-center justify-between mb-4">
                                <div className="text-sm text-muted-foreground">
                                  {visibleLocations.length === 0
                                    ? 'No locations added yet'
                                    : `${visibleLocations.length} location${visibleLocations.length === 1 ? '' : 's'} configured`}
                                </div>
                                <Dialog
                                  open={isAddLocationDialogOpen}
                                  onOpenChange={setIsAddLocationDialogOpen}
                                >
                                  <DialogTrigger asChild>
                                    <Button type="button" variant="outline" size="sm">
                                      <Plus className="h-4 w-4 mr-2" />
                                      Add Location
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                      <DialogTitle>Add Approved Location</DialogTitle>
                                      <DialogDescription>
                                        Click on the map to select a location, or enter coordinates
                                        manually. Provide a name for the location.
                                      </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4">
                                      <div className="space-y-2">
                                        <Label htmlFor="location-name">Location Name *</Label>
                                        <Input
                                          id="location-name"
                                          value={newLocationName}
                                          onChange={(e) => setNewLocationName(e.target.value)}
                                          placeholder="e.g., Main Office"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label htmlFor="location-radius">
                                          Radius (meters, optional; defaults to 50)
                                        </Label>
                                        <Input
                                          id="location-radius"
                                          type="number"
                                          min={1}
                                          step={1}
                                          value={newLocationRadius}
                                          onChange={(e) => setNewLocationRadius(e.target.value)}
                                          placeholder="50"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Select Location on Map</Label>
                                        <div
                                          className="border rounded-lg overflow-hidden"
                                          style={{ height: '400px' }}
                                        >
                                          <APIProvider apiKey={envWeb.VITE_PUBLIC_MAPS_API_KEY}>
                                            <Map
                                              style={{ width: '100%', height: '100%' }}
                                              defaultCenter={{
                                                lat: newLocationLat
                                                  ? Number(newLocationLat)
                                                  : 14.5995,
                                                lng: newLocationLong
                                                  ? Number(newLocationLong)
                                                  : 120.9842,
                                              }}
                                              defaultZoom={15}
                                              gestureHandling={'cooperative'}
                                              disableDefaultUI={false}
                                              mapId={`add-location-map-${clientId}`}
                                              onClick={(e) => {
                                                if (e.detail.latLng) {
                                                  setNewLocationLat(e.detail.latLng.lat.toString())
                                                  setNewLocationLong(
                                                    e.detail.latLng.lng.toString(),
                                                  )
                                                }
                                              }}
                                            >
                                              {newLocationLat && newLocationLong && (
                                                <AdvancedMarker
                                                  position={{
                                                    lat: Number(newLocationLat),
                                                    lng: Number(newLocationLong),
                                                  }}
                                                />
                                              )}
                                            </Map>
                                          </APIProvider>
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          Click on the map to select a location, or enter
                                          coordinates manually below.
                                        </p>
                                      </div>
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label htmlFor="location-lat">Latitude</Label>
                                          <Input
                                            id="location-lat"
                                            type="number"
                                            step="any"
                                            value={newLocationLat}
                                            onChange={(e) => {
                                              setNewLocationLat(e.target.value)
                                            }}
                                            placeholder="14.5995"
                                          />
                                        </div>
                                        <div className="space-y-2">
                                          <Label htmlFor="location-long">Longitude</Label>
                                          <Input
                                            id="location-long"
                                            type="number"
                                            step="any"
                                            value={newLocationLong}
                                            onChange={(e) => {
                                              setNewLocationLong(e.target.value)
                                            }}
                                            placeholder="120.9842"
                                          />
                                        </div>
                                      </div>
                                      {newLocationLat && newLocationLong && (
                                        <div className="text-xs text-muted-foreground font-mono">
                                          Selected: {Number(newLocationLat).toFixed(6)},{' '}
                                          {Number(newLocationLong).toFixed(6)}
                                        </div>
                                      )}
                                    </div>
                                    <DialogFooter className="gap-2">
                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          setIsAddLocationDialogOpen(false)
                                          setNewLocationLat('')
                                          setNewLocationLong('')
                                          setNewLocationName('')
                                          setNewLocationRadius('50')
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          if (
                                            !newLocationLat ||
                                            !newLocationLong ||
                                            !newLocationName.trim()
                                          ) {
                                            toast.error(
                                              'Please provide location name and coordinates',
                                            )
                                            return
                                          }
                                          const radiusValue =
                                            newLocationRadius.trim().length > 0
                                              ? Number(newLocationRadius)
                                              : undefined
                                          const location = {
                                            lat: Number(newLocationLat),
                                            long: Number(newLocationLong),
                                            name: newLocationName.trim(),
                                            radiusMeters:
                                              radiusValue && !Number.isNaN(radiusValue)
                                                ? radiusValue
                                                : undefined,
                                          }
                                          const currentLocations = field.value ?? []
                                          const newLocations = [...currentLocations, location]
                                          field.onChange(newLocations)
                                          setIsAddLocationDialogOpen(false)
                                          setNewLocationLat('')
                                          setNewLocationLong('')
                                          setNewLocationName('')

                                          setNewLocationRadius('50')
                                          // Auto-save
                                          const currentSetting = settings?.find(
                                            (s) => s.key === 'approved_locations',
                                          )
                                          if (currentSetting) {
                                            updateSettingMutation.mutate({
                                              clientSettingsId: currentSetting.clientSettingsId,
                                              value: newLocations,
                                              validFrom: DateTime.now()
                                                .minus({ years: 10 })
                                                .toJSDate(),
                                              validTo: DateTime.now()
                                                .plus({ years: 10 })
                                                .toJSDate(),
                                            })
                                          } else {
                                            createSettingMutation.mutate({
                                              clientId,
                                              key: 'approved_locations',
                                              value: newLocations,
                                              validFrom: DateTime.now()
                                                .minus({ years: 10 })
                                                .toJSDate(),
                                              validTo: DateTime.now()
                                                .plus({ years: 10 })
                                                .toJSDate(),
                                            })
                                          }
                                        }}
                                        disabled={
                                          !newLocationLat ||
                                          !newLocationLong ||
                                          !newLocationName.trim()
                                        }
                                      >
                                        Save Location
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                              </div>
                              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                                {visibleLocations.map((location, idx) => (
                                  <div
                                    key={idx}
                                    className="border rounded-lg p-4 space-y-3 bg-card hover:bg-muted/50 transition-colors"
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <MapPin className="h-4 w-4 text-muted-foreground" />
                                          <h4 className="font-semibold text-sm">
                                            {location.name}
                                          </h4>
                                        </div>
                                        <div className="text-xs text-muted-foreground font-mono mb-2">
                                          {location.lat.toFixed(6)}, {location.long.toFixed(6)}
                                          {location.radiusMeters != null && (
                                            <span className="ml-2">
                                              · Radius: {location.radiusMeters}m
                                            </span>
                                          )}
                                        </div>
                                        <div
                                          className="border rounded-lg overflow-hidden"
                                          style={{ height: '150px' }}
                                        >
                                          <APIProvider apiKey={envWeb.VITE_PUBLIC_MAPS_API_KEY}>
                                            <Map
                                              style={{ width: '100%', height: '100%' }}
                                              defaultCenter={{
                                                lat: location.lat,
                                                lng: location.long,
                                              }}
                                              defaultZoom={15}
                                              gestureHandling={'cooperative'}
                                              disableDefaultUI={true}
                                              mapId={`map-approved-location-${idx}-${clientId}`}
                                            >
                                              <AdvancedMarker
                                                position={{
                                                  lat: location.lat,
                                                  lng: location.long,
                                                }}
                                              />
                                            </Map>
                                          </APIProvider>
                                        </div>
                                      </div>
                                      <div className="flex gap-2 ml-4">
                                        <Dialog
                                          open={editingLocationIndex === idx}
                                          onOpenChange={(open) => {
                                            if (!open) {
                                              setEditingLocationIndex(null)
                                              setEditLocationLat('')
                                              setEditLocationLong('')
                                              setEditLocationName('')
                                              setEditLocationRadius('')
                                            } else {
                                              setEditLocationLat('')
                                              setEditLocationLong('')
                                              setEditLocationName('')
                                              setEditLocationRadius(
                                                location.radiusMeters != null
                                                  ? String(location.radiusMeters)
                                                  : '',
                                              )
                                            }
                                          }}
                                        >
                                          <DialogTrigger asChild>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                setEditingLocationIndex(idx)
                                                setEditLocationLat('')
                                                setEditLocationLong('')
                                                setEditLocationName('')
                                              }}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                          </DialogTrigger>
                                          <DialogContent className="max-w-2xl">
                                            <DialogHeader>
                                              <DialogTitle>Edit Location</DialogTitle>
                                              <DialogDescription>
                                                Click on the map to update the location, or enter
                                                coordinates manually. Update the name if needed.
                                              </DialogDescription>
                                            </DialogHeader>
                                            <div className="space-y-4">
                                              <div className="space-y-2">
                                                <Label htmlFor="edit-location-name">
                                                  Location Name *
                                                </Label>
                                                <Input
                                                  id="edit-location-name"
                                                  value={editLocationName || location.name}
                                                  onChange={(e) =>
                                                    setEditLocationName(e.target.value)
                                                  }
                                                  placeholder="e.g., Main Office"
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label htmlFor="edit-location-radius">
                                                  Radius (meters, optional; defaults to 50)
                                                </Label>
                                                <Input
                                                  id="edit-location-radius"
                                                  type="number"
                                                  min={1}
                                                  step={1}
                                                  value={
                                                    editLocationRadius ||
                                                    (location.radiusMeters != null
                                                      ? String(location.radiusMeters)
                                                      : '')
                                                  }
                                                  onChange={(e) => {
                                                    setEditLocationRadius(e.target.value)
                                                  }}
                                                  placeholder="50"
                                                />
                                              </div>
                                              <div className="space-y-2">
                                                <Label>Select Location on Map</Label>
                                                <div
                                                  className="border rounded-lg overflow-hidden"
                                                  style={{ height: '400px' }}
                                                >
                                                  <APIProvider
                                                    apiKey={envWeb.VITE_PUBLIC_MAPS_API_KEY}
                                                  >
                                                    <Map
                                                      style={{ width: '100%', height: '100%' }}
                                                      defaultCenter={{
                                                        lat: editLocationLat
                                                          ? Number(editLocationLat)
                                                          : location.lat,
                                                        lng: editLocationLong
                                                          ? Number(editLocationLong)
                                                          : location.long,
                                                      }}
                                                      defaultZoom={15}
                                                      gestureHandling={'cooperative'}
                                                      disableDefaultUI={false}
                                                      mapId={`edit-location-map-${idx}-${clientId}`}
                                                      onClick={(e) => {
                                                        if (e.detail.latLng) {
                                                          setEditLocationLat(
                                                            e.detail.latLng.lat.toString(),
                                                          )
                                                          setEditLocationLong(
                                                            e.detail.latLng.lng.toString(),
                                                          )
                                                        }
                                                      }}
                                                    >
                                                      {editLocationLat && editLocationLong ? (
                                                        <AdvancedMarker
                                                          position={{
                                                            lat: Number(editLocationLat),
                                                            lng: Number(editLocationLong),
                                                          }}
                                                        />
                                                      ) : (
                                                        <AdvancedMarker
                                                          position={{
                                                            lat: location.lat,
                                                            lng: location.long,
                                                          }}
                                                        />
                                                      )}
                                                    </Map>
                                                  </APIProvider>
                                                </div>
                                                <p className="text-xs text-muted-foreground">
                                                  Click on the map to update the location, or enter
                                                  coordinates manually below.
                                                </p>
                                              </div>
                                              <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                  <Label htmlFor="edit-location-lat">
                                                    Latitude
                                                  </Label>
                                                  <Input
                                                    id="edit-location-lat"
                                                    type="number"
                                                    step="any"
                                                    value={
                                                      editLocationLat || location.lat.toString()
                                                    }
                                                    onChange={(e) => {
                                                      setEditLocationLat(e.target.value)
                                                    }}
                                                    placeholder="14.5995"
                                                  />
                                                </div>
                                                <div className="space-y-2">
                                                  <Label htmlFor="edit-location-long">
                                                    Longitude
                                                  </Label>
                                                  <Input
                                                    id="edit-location-long"
                                                    type="number"
                                                    step="any"
                                                    value={
                                                      editLocationLong || location.long.toString()
                                                    }
                                                    onChange={(e) => {
                                                      setEditLocationLong(e.target.value)
                                                    }}
                                                    placeholder="120.9842"
                                                  />
                                                </div>
                                              </div>
                                              {(editLocationLat || editLocationLong) && (
                                                <div className="text-xs text-muted-foreground font-mono">
                                                  Selected:{' '}
                                                  {Number(editLocationLat || location.lat).toFixed(
                                                    6,
                                                  )}
                                                  ,{' '}
                                                  {Number(
                                                    editLocationLong || location.long,
                                                  ).toFixed(6)}
                                                </div>
                                              )}
                                            </div>
                                            <DialogFooter className="gap-2">
                                              <Button
                                                variant="outline"
                                                onClick={() => {
                                                  setEditingLocationIndex(null)
                                                  setEditLocationLat('')
                                                  setEditLocationLong('')
                                                  setEditLocationName('')
                                                  setEditLocationRadius('')
                                                }}
                                              >
                                                Cancel
                                              </Button>
                                              <Button
                                                onClick={() => {
                                                  const finalLat = editLocationLat
                                                    ? Number(editLocationLat)
                                                    : location.lat
                                                  const finalLong = editLocationLong
                                                    ? Number(editLocationLong)
                                                    : location.long
                                                  const finalName =
                                                    editLocationName.trim() || location.name
                                                  const radiusValue =
                                                    editLocationRadius.trim().length > 0
                                                      ? Number(editLocationRadius)
                                                      : undefined

                                                  if (!finalName.trim()) {
                                                    toast.error('Please provide a location name')
                                                    return
                                                  }

                                                  const updatedLocation = {
                                                    lat: finalLat,
                                                    long: finalLong,
                                                    name: finalName,
                                                    radiusMeters:
                                                      radiusValue && !Number.isNaN(radiusValue)
                                                        ? radiusValue
                                                        : location.radiusMeters,
                                                  }
                                                  const currentLocations = field.value ?? []
                                                  const newLocations = [...currentLocations]
                                                  newLocations[idx] = updatedLocation
                                                  field.onChange(newLocations)
                                                  setEditingLocationIndex(null)
                                                  setEditLocationLat('')
                                                  setEditLocationLong('')
                                                  setEditLocationName('')
                                                  setEditLocationRadius('')
                                                  // Auto-save
                                                  const currentSetting = settings?.find(
                                                    (s) => s.key === 'approved_locations',
                                                  )
                                                  if (currentSetting) {
                                                    updateSettingMutation.mutate({
                                                      clientSettingsId:
                                                        currentSetting.clientSettingsId,
                                                      value: newLocations,
                                                      validFrom: DateTime.now()
                                                        .minus({ years: 10 })
                                                        .toJSDate(),
                                                      validTo: DateTime.now()
                                                        .plus({ years: 10 })
                                                        .toJSDate(),
                                                    })
                                                  } else {
                                                    createSettingMutation.mutate({
                                                      clientId,
                                                      key: 'approved_locations',
                                                      value: newLocations,
                                                      validFrom: DateTime.now()
                                                        .minus({ years: 10 })
                                                        .toJSDate(),
                                                      validTo: DateTime.now()
                                                        .plus({ years: 10 })
                                                        .toJSDate(),
                                                    })
                                                  }
                                                }}
                                                disabled={
                                                  !editLocationName.trim() && !location.name
                                                }
                                              >
                                                Save Changes
                                              </Button>
                                            </DialogFooter>
                                          </DialogContent>
                                        </Dialog>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setDeletingLocationIndex(idx)}
                                        >
                                          <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                        <AlertDialog
                                          open={deletingLocationIndex === idx}
                                          onOpenChange={(open) => {
                                            if (!open) {
                                              setDeletingLocationIndex(null)
                                            }
                                          }}
                                        >
                                          <AlertDialogContent>
                                            <AlertDialogHeader>
                                              <AlertDialogTitle>Delete Location</AlertDialogTitle>
                                              <AlertDialogDescription>
                                                Are you sure you want to delete &quot;
                                                {location.name}&quot;? This action cannot be
                                                undone.
                                              </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                                              <AlertDialogAction
                                                onClick={() => {
                                                  const currentLocations = field.value ?? []
                                                  const newLocations = currentLocations.filter(
                                                    (_, i) => i !== idx,
                                                  )
                                                  field.onChange(newLocations)
                                                  setDeletingLocationIndex(null)
                                                  // Auto-save
                                                  const currentSetting = settings?.find(
                                                    (s) => s.key === 'approved_locations',
                                                  )
                                                  if (currentSetting) {
                                                    if (newLocations.length === 0) {
                                                      deleteSettingMutation.mutate({
                                                        clientSettingsId:
                                                          currentSetting.clientSettingsId,
                                                      })
                                                    } else {
                                                      updateSettingMutation.mutate({
                                                        clientSettingsId:
                                                          currentSetting.clientSettingsId,
                                                        value: newLocations,
                                                        validFrom: DateTime.now()
                                                          .minus({ years: 10 })
                                                          .toJSDate(),
                                                        validTo: DateTime.now()
                                                          .plus({ years: 10 })
                                                          .toJSDate(),
                                                      })
                                                    }
                                                  }
                                                }}
                                                className="bg-destructive hover:bg-destructive/90"
                                              >
                                                Delete
                                              </AlertDialogAction>
                                            </AlertDialogFooter>
                                          </AlertDialogContent>
                                        </AlertDialog>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                        <FormControl>
                          <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
                            Select a date, then click the &quot;View Locations&quot; button above
                            to manage approved locations.
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card>

        {/* Government Contributions */}
        <Card className="max-w-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <CardTitle className="text-lg">Government Contributions</CardTitle>
            </div>
            <CardDescription>Optional overrides for statutory contributions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Fixed SSS contribution */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Fixed SSS Contribution (Monthly)</h3>
              {(() => {
                const effectiveValue = getEffectiveClientSettingValue(
                  'fixed_sss_contribution_amount',
                )
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3>Employee SSS Amount</h3>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={effectiveValue ?? ''} disabled />
                        {settings && (
                          <ClientEffectiveSettingsCalendar
                            clientId={clientId}
                            clientName={client?.name}
                            settingKey="fixed_sss_contribution_amount"
                            settingDisplayName="Fixed SSS Contribution"
                            allSettings={settings}
                            onCreateOrUpdateSetting={() => {
                              queryClient.invalidateQueries({
                                queryKey: api.clientSettings.getClientSettings.queryKey(),
                              })
                              if (selectedDate) {
                                clientSettingsForDateQuery.refetch()
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}
              <div className="text-xs text-muted-foreground mt-2">
                When set, this fixed amount is used instead of bracket-based SSS.
              </div>
            </div>

            {/* Fixed PhilHealth contribution */}
            <div>
              <h3 className="text-sm font-semibold mb-3">
                Fixed PhilHealth Contribution (Monthly)
              </h3>
              {(() => {
                const effectiveValue = getEffectiveClientSettingValue(
                  'fixed_phic_contribution_amount',
                )
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3>Employee PhilHealth Amount</h3>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={effectiveValue ?? ''} disabled />
                        {settings && (
                          <ClientEffectiveSettingsCalendar
                            clientId={clientId}
                            clientName={client?.name}
                            settingKey="fixed_phic_contribution_amount"
                            settingDisplayName="Fixed PhilHealth Contribution"
                            allSettings={settings}
                            onCreateOrUpdateSetting={() => {
                              queryClient.invalidateQueries({
                                queryKey: api.clientSettings.getClientSettings.queryKey(),
                              })
                              if (selectedDate) {
                                clientSettingsForDateQuery.refetch()
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}
              <div className="text-xs text-muted-foreground mt-2">
                When set, this fixed amount is used instead of bracket-based PhilHealth.
              </div>
            </div>

            <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
              <span className="font-medium">Monthly amount:</span> This value is for the full
              month. For example, if the pay cycle is 2x a month, half of this amount is used for
              each cutoff&apos;s calculation.
            </div>
          </CardContent>
        </Card>

        {/* Allowance Settings (New) */}
        <Card className="max-w-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              <CardTitle className="text-lg flex items-center gap-2">
                <span>Allowance Settings</span>
                <NewBadge variant="chip" />
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 text-muted-foreground hover:text-foreground"
                    >
                      <Info className="h-4 w-4" />
                      <span className="sr-only">How does Taxable/Non-taxable work?</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Taxable vs. non-taxable allowances</DialogTitle>
                      <DialogDescription>
                        How the Taxable toggle and non-taxable cap affect withholding tax.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                      <div>
                        <div className="font-medium mb-1">Taxable (default)</div>
                        <p className="text-muted-foreground">
                          The full amount is added to taxable income and counts toward withholding
                          tax, same as before this option existed.
                        </p>
                      </div>
                      <div>
                        <div className="font-medium mb-1">Non-taxable, no cap</div>
                        <p className="text-muted-foreground">
                          The full amount is excluded from taxable income. It still counts as real
                          pay (gross pay is unaffected) — it just isn&apos;t taxed.
                        </p>
                      </div>
                      <div>
                        <div className="font-medium mb-1">Non-taxable, with a cap</div>
                        <p className="text-muted-foreground">
                          Only the amount above the cap is added to taxable income. For example, a
                          ₱3,000 allowance with a ₱2,500 cap adds ₱500 to taxable income — the
                          remaining ₱2,500 stays untaxed.
                        </p>
                      </div>
                      <p className="text-muted-foreground text-xs italic">
                        The cap should reflect an actual legal limit for that type of benefit (e.g.
                        BIR de minimis rules) — this isn&apos;t enforced automatically, so choose
                        it carefully.
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </div>
            <CardDescription>
              Configure allowance with frequency and amount in a single setting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(() => {
              const activeAllowances = getAllActiveClientAllowanceSettings(settings, selectedDate)

              if (activeAllowances.length === 0) {
                return (
                  <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                      No allowance settings active for{' '}
                      {selectedDate
                        ? DateTime.fromJSDate(selectedDate).toFormat('yyyy LLL dd')
                        : 'today'}
                      .
                    </div>
                    {settings && (
                      <ClientEffectiveSettingsCalendar
                        clientId={clientId}
                        clientName={client?.name}
                        settingKey="allowance_settings"
                        settingDisplayName="Allowance Settings"
                        allSettings={settings}
                        onCreateOrUpdateSetting={() => {
                          queryClient.invalidateQueries({
                            queryKey: api.clientSettings.getClientSettings.queryKey(),
                          })
                          if (selectedDate) {
                            clientSettingsForDateQuery.refetch()
                          }
                        }}
                      />
                    )}
                  </div>
                )
              }

              return (
                <div className="space-y-4">
                  <div className="space-y-2">
                    {activeAllowances.map((allowance, index) => (
                      <div
                        key={allowance.clientSettingsId ?? index}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{allowance.value.name}</h4>
                            {allowance.value.isTaxable === false ? (
                              <Badge variant="secondary">
                                Non-taxable
                                {allowance.value.nonTaxableCap !== undefined
                                  ? ` (cap ₱${allowance.value.nonTaxableCap.toLocaleString()})`
                                  : ''}
                              </Badge>
                            ) : (
                              <Badge variant="outline">Taxable</Badge>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground">Frequency</Label>
                            <div className="text-sm font-medium capitalize">
                              {allowance.value.frequency}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Amount</Label>
                            <div className="text-sm font-medium">
                              ₱{allowance.value.amount.toLocaleString()}
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Valid from{' '}
                          {DateTime.fromJSDate(allowance.validFrom).toFormat('yyyy LLL dd')}
                          {allowance.validTo
                            ? ` to ${DateTime.fromJSDate(allowance.validTo).toFormat('yyyy LLL dd')}`
                            : ' (ongoing)'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="text-xs text-muted-foreground">
                      Multiple allowances can be active simultaneously. All active allowances will
                      be summed in payroll calculations.
                    </div>
                    {settings && (
                      <ClientEffectiveSettingsCalendar
                        clientId={clientId}
                        clientName={client?.name}
                        settingKey="allowance_settings"
                        settingDisplayName="Allowance Settings"
                        allSettings={settings}
                        onCreateOrUpdateSetting={() => {
                          queryClient.invalidateQueries({
                            queryKey: api.clientSettings.getClientSettings.queryKey(),
                          })
                          if (selectedDate) {
                            clientSettingsForDateQuery.refetch()
                          }
                        }}
                      />
                    )}
                  </div>
                </div>
              )
            })()}
          </CardContent>
        </Card>

        {/* Break Settings Card */}
        {/* <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Coffee className="h-4 w-4" />
              <CardTitle className="text-lg text-red-400 line-through">
                Break Settings (Deprecated)
              </CardTitle>
            </div>
            <CardDescription>Configure break settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...breakForm}>
              <form
                // onSubmit={breakForm.handleSubmit((values) => {
                // updateBreakSettingsMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-6"
              >
                <FormField
                  control={breakForm.control}
                  name="breakTimeDayMins"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('break_time_day_mins')
                    return (
                      <FormItem>
                        <FormLabel>Break Time (day shift) (mins)</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={effectiveValue ?? ''}
                              disabled
                            />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="break_time_day_mins"
                                  settingDisplayName="Break Time (day shift) (mins)"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />

                <FormField
                  control={breakForm.control}
                  name="breakTimeNightMins"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('break_time_night_mins')
                    return (
                      <FormItem>
                        <FormLabel>Break Time (night shift) (mins)</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Input
                              type="number"
                              min={0}
                              step={1}
                              value={effectiveValue ?? ''}
                              disabled
                            />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="break_time_night_mins"
                                  settingDisplayName="Break Time (night shift) (mins)"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />

                <FormField
                  control={breakForm.control}
                  name="isPaidBreak"
                  render={() => {
                    const effectiveValue = getEffectiveClientSettingValue('is_paid_break')
                    return (
                      <FormItem>
                        <FormLabel>Is Paid Break</FormLabel>
                        <FormControl>
                          <div className="flex items-center justify-between">
                            <Switch checked={effectiveValue} disabled />
                            {settings && (
                              <>
                                <ClientEffectiveSettingsCalendar
                                  clientId={clientId}
                                  clientName={client?.name}
                                  settingKey="is_paid_break"
                                  settingDisplayName="Is Paid Break"
                                  allSettings={settings}
                                  onCreateOrUpdateSetting={() => {
                                    queryClient.invalidateQueries({
                                      queryKey: api.clientSettings.getClientSettings.queryKey(),
                                    })
                                    if (selectedDate) {
                                      clientSettingsForDateQuery.refetch()
                                    }
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
              </form>
            </Form>
          </CardContent>
        </Card> */}
      </div>

      <Card className="col-span-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            <CardTitle className="text-lg">Pay Multipliers</CardTitle>
          </div>
          <CardDescription>
            Configure all pay multipliers for different scenarios (max of 4 decimal places)
          </CardDescription>
          {/* <CardDescription className="flex items-center gap-x-2 mt-4">
            Note: In order to avoid duplication of hours in the records, differential pay (e.g. ND,
            OT ND) is calculated solely based on the hours rendered during this time (i.e. not on
            top of the already rendered regular hours). This means all differential multipliers
            will start with 1.
          </CardDescription> */}
          {/* <CardDescription>For example, if OT = 1.25, ND = 0.1</CardDescription>
          <CardDescription>then, OT ND = 1.25 * (1 + 0.10) = 1.25 * 1.10 = 1.375</CardDescription> */}
        </CardHeader>
        <CardContent>
          {/* Regular Day Multipliers */}
          <div className="mb-6 border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Regular Day Multipliers</h3>
            <Form {...regularDayMultipliersForm}>
              <form
                // onSubmit={regularDayMultipliersForm.handleSubmit((values) => {
                // updateRegularDayMultipliersMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <FormField
                    control={regularDayMultipliersForm.control}
                    name="regularHoursMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_hours_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regular Hours (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_hours_multiplier"
                                    settingDisplayName="Regular Hours (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularDayMultipliersForm.control}
                    name="overtimeMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('overtime_multiplier')
                      return (
                        <FormItem>
                          <FormLabel>Overtime (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="overtime_multiplier"
                                    settingDisplayName="Overtime (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularDayMultipliersForm.control}
                    name="nightDifferentialMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'night_differential_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Night Differential (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="night_differential_multiplier"
                                    settingDisplayName="Night Differential (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularDayMultipliersForm.control}
                    name="otNdHoursMultiplier"
                    render={() => {
                      const effectiveValue =
                        getEffectiveClientSettingValue('ot_nd_hours_multiplier')
                      return (
                        <FormItem>
                          <FormLabel>OT Night Differential (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="ot_nd_hours_multiplier"
                                    settingDisplayName="OT Night Differential (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>

          {/* Rest Day Multipliers */}
          <div className="mb-6 border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Rest Day Multipliers</h3>
            <Form {...restDayMultipliersForm}>
              <form
                // onSubmit={restDayMultipliersForm.handleSubmit((values) => {
                // updateRestDayMultipliersMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <FormField
                    control={restDayMultipliersForm.control}
                    name="restDayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue('rest_day_multiplier')
                      return (
                        <FormItem>
                          <FormLabel>Rest Day (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="rest_day_multiplier"
                                    settingDisplayName="Rest Day (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={restDayMultipliersForm.control}
                    name="restDayNdMultiplier"
                    render={() => {
                      const effectiveValue =
                        getEffectiveClientSettingValue('rest_day_nd_multiplier')
                      return (
                        <FormItem>
                          <FormLabel>Rest Day ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="rest_day_nd_multiplier"
                                    settingDisplayName="Rest Day ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={restDayMultipliersForm.control}
                    name="restDayOtMultiplier"
                    render={() => {
                      const effectiveValue =
                        getEffectiveClientSettingValue('rest_day_ot_multiplier')
                      return (
                        <FormItem>
                          <FormLabel>Rest Day OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="rest_day_ot_multiplier"
                                    settingDisplayName="Rest Day OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={restDayMultipliersForm.control}
                    name="restDayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'rest_day_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Rest Day OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="rest_day_ot_nd_multiplier"
                                    settingDisplayName="Rest Day OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>

          {/* Special Holiday Multipliers */}
          <div className="mb-6 border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Special Holiday Multipliers</h3>
            <Form {...specialHolidayMultipliersForm}>
              <form
                // onSubmit={specialHolidayMultipliersForm.handleSubmit((values) => {
                // updateSpecialHolidayMultipliersMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Special Holiday (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_multiplier"
                                    settingDisplayName="Special Holiday (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Special Holiday ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_nd_multiplier"
                                    settingDisplayName="Special Holiday ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Special Holiday OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_ot_multiplier"
                                    settingDisplayName="Special Holiday OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Special Holiday OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_ot_nd_multiplier"
                                    settingDisplayName="Special Holiday OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayRestDayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_rest_day_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>SH Rest Day (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_rest_day_multiplier"
                                    settingDisplayName="SH Rest Day (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayRestDayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_rest_day_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>SH Rest Day ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_rest_day_nd_multiplier"
                                    settingDisplayName="SH Rest Day ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayRestDayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_rest_day_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>SH Rest Day OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_rest_day_ot_multiplier"
                                    settingDisplayName="SH Rest Day OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={specialHolidayMultipliersForm.control}
                    name="specialHolidayRestDayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'special_holiday_rest_day_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>SH Rest Day OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="special_holiday_rest_day_ot_nd_multiplier"
                                    settingDisplayName="SH Rest Day OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>

          {/* Regular Holiday Multipliers */}
          <div className="mb-6 border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Regular Holiday Multipliers</h3>
            <Form {...regularHolidayMultipliersForm}>
              <form
                // onSubmit={regularHolidayMultipliersForm.handleSubmit((values) => {
                // updateRegularHolidayMultipliersMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regular Holiday (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_multiplier"
                                    settingDisplayName="Regular Holiday (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regular Holiday ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_nd_multiplier"
                                    settingDisplayName="Regular Holiday ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regular Holiday OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_ot_multiplier"
                                    settingDisplayName="Regular Holiday OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regular Holiday OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_ot_nd_multiplier"
                                    settingDisplayName="Regular Holiday OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayRestDayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_rest_day_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>RH Rest Day (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_rest_day_multiplier"
                                    settingDisplayName="RH Rest Day (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayRestDayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_rest_day_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>RH Rest Day ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_rest_day_nd_multiplier"
                                    settingDisplayName="RH Rest Day ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayRestDayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_rest_day_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>RH Rest Day OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_rest_day_ot_multiplier"
                                    settingDisplayName="RH Rest Day OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regularHolidayMultipliersForm.control}
                    name="regularHolidayRestDayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regular_holiday_rest_day_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>RH Rest Day OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regular_holiday_rest_day_ot_nd_multiplier"
                                    settingDisplayName="RH Rest Day OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>

          {/* Regional Holiday Multipliers */}
          <div className="mb-6 border rounded-lg p-4">
            <h3 className="font-semibold mb-4">Regional Holiday Multipliers</h3>
            <Form {...regionalHolidayMultipliersForm}>
              <form
                // onSubmit={regionalHolidayMultipliersForm.handleSubmit((values) => {
                // updateRegionalHolidayMultipliersMutation.mutate({
                //   settings: values,
                //   validFrom: undefined,
                //   clientId,
                // })
                // })}
                className="space-y-4"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_multiplier"
                                    settingDisplayName="Regional Holiday (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_nd_multiplier"
                                    settingDisplayName="Regional Holiday ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_ot_multiplier"
                                    settingDisplayName="Regional Holiday OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_ot_nd_multiplier"
                                    settingDisplayName="Regional Holiday OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayRestDayMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_rest_day_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday Rest Day (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_rest_day_multiplier"
                                    settingDisplayName="Regional Holiday Rest Day (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayRestDayNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_rest_day_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday Rest Day ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_rest_day_nd_multiplier"
                                    settingDisplayName="Regional Holiday Rest Day ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayRestDayOtMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_rest_day_ot_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday Rest Day OT (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_rest_day_ot_multiplier"
                                    settingDisplayName="Regional Holiday Rest Day OT (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                  <FormField
                    control={regionalHolidayMultipliersForm.control}
                    name="regionalHolidayRestDayOtNdMultiplier"
                    render={() => {
                      const effectiveValue = getEffectiveClientSettingValue(
                        'regional_holiday_rest_day_ot_nd_multiplier',
                      )
                      return (
                        <FormItem>
                          <FormLabel>Regional Holiday Rest Day OT ND (x)</FormLabel>
                          <FormControl>
                            <div className="flex items-center justify-between">
                              <Input
                                type="number"
                                step="0.0001"
                                value={effectiveValue ?? ''}
                                disabled
                              />
                              {settings && (
                                <>
                                  <ClientEffectiveSettingsCalendar
                                    clientId={clientId}
                                    clientName={client?.name}
                                    settingKey="regional_holiday_rest_day_ot_nd_multiplier"
                                    settingDisplayName="Regional Holiday Rest Day OT ND (x)"
                                    allSettings={settings}
                                    onCreateOrUpdateSetting={() => {
                                      queryClient.invalidateQueries({
                                        queryKey: api.clientSettings.getClientSettings.queryKey(),
                                      })
                                      if (selectedDate) {
                                        clientSettingsForDateQuery.refetch()
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )
                    }}
                  />
                </div>
              </form>
            </Form>
          </div>
        </CardContent>
      </Card>

      {/* Notification Preferences Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <CardTitle className="text-lg">Missing Clock-In Notifications</CardTitle>
          </div>
          <CardDescription>
            Configure automated notifications when employees haven't clocked in
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="notification-enabled"
              checked={notificationEnabled}
              onCheckedChange={(checked) => setNotificationEnabled(checked === true)}
            />
            <Label htmlFor="notification-enabled">Enable notifications</Label>
          </div>

          {notificationEnabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <Label>Notification Slots</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Add a new slot with default values
                      const lastSlot = notificationSlots[notificationSlots.length - 1]
                      const newStart = lastSlot?.timeRangeEnd ?? '09:00'
                      // Default new end time to 4 hours after start
                      const [hours] = newStart.split(':').map(Number)
                      const newEndHours = Math.min((hours ?? 0) + 4, 23)
                      const newEnd = `${String(newEndHours).padStart(2, '0')}:00`
                      setNotificationSlots([
                        ...notificationSlots,
                        {
                          notificationTime: newEnd,
                          timeRangeStart: newStart,
                          timeRangeEnd: newEnd,
                        },
                      ])
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Slot
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Each slot checks for clock-ins within its time range and sends a notification at
                  the specified time.
                </p>

                <div className="space-y-3">
                  {notificationSlots.map((slot, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-2 p-3 border rounded-md bg-muted/30"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Slot {index + 1}</span>
                        {notificationSlots.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            onClick={() => {
                              setNotificationSlots(notificationSlots.filter((_, i) => i !== index))
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Time Range Start</Label>
                          <Input
                            type="time"
                            value={slot.timeRangeStart}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, timeRangeStart: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Time Range End</Label>
                          <Input
                            type="time"
                            value={slot.timeRangeEnd}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, timeRangeEnd: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label className="text-xs">Notify At</Label>
                          <Input
                            type="time"
                            value={slot.notificationTime}
                            onChange={(e) => {
                              const updated = [...notificationSlots]
                              updated[index] = { ...slot, notificationTime: e.target.value }
                              setNotificationSlots(updated)
                            }}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Checks for clock-ins between {slot.timeRangeStart} and {slot.timeRangeEnd},
                        notifies at {slot.notificationTime}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Advance minutes</Label>
                <Input
                  type="number"
                  value={advanceMinutes}
                  onChange={(e) => setAdvanceMinutes(Number.parseInt(e.target.value, 10) || 0)}
                />
                <p className="text-xs text-muted-foreground">
                  Send notification this many minutes before each notification time
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Notification methods</Label>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-email"
                      checked={
                        notificationMethods.includes('email') ||
                        notificationMethods.includes('both')
                      }
                      onCheckedChange={(checked) => {
                        const hasEmail =
                          notificationMethods.includes('email') ||
                          notificationMethods.includes('both')
                        const hasSms =
                          notificationMethods.includes('sms') ||
                          notificationMethods.includes('both')

                        if (checked) {
                          // Adding email
                          if (hasSms) {
                            // If SMS is already selected, change to 'both'
                            setNotificationMethods(['both'])
                          } else {
                            // Otherwise just add 'email'
                            setNotificationMethods(['email'])
                          }
                        } else {
                          // Removing email
                          if (hasSms) {
                            // If SMS is still selected, keep only SMS
                            setNotificationMethods(['sms'])
                          } else {
                            // Otherwise, must have at least one method
                            setNotificationMethods(['email'])
                          }
                        }
                      }}
                    />
                    <Label htmlFor="method-email">Email</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="method-sms"
                      checked={
                        notificationMethods.includes('sms') || notificationMethods.includes('both')
                      }
                      onCheckedChange={(checked) => {
                        const hasEmail =
                          notificationMethods.includes('email') ||
                          notificationMethods.includes('both')
                        const hasSms =
                          notificationMethods.includes('sms') ||
                          notificationMethods.includes('both')

                        if (checked) {
                          // Adding SMS
                          if (hasEmail) {
                            // If email is already selected, change to 'both'
                            setNotificationMethods(['both'])
                          } else {
                            // Otherwise just add 'sms'
                            setNotificationMethods(['sms'])
                          }
                        } else {
                          // Removing SMS
                          if (hasEmail) {
                            // If email is still selected, keep only email
                            setNotificationMethods(['email'])
                          } else {
                            // Otherwise, must have at least one method
                            setNotificationMethods(['sms'])
                          }
                        }
                      }}
                    />
                    <Label htmlFor="method-sms">SMS</Label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient emails</Label>
                <TagInputMyCustomComp
                  value={recipientEmails}
                  onChange={(emails) => {
                    setRecipientEmails(emails)
                    setEmailValidationError(null)
                  }}
                  placeholder="Type email and press Enter to add"
                  validate={validateEmail}
                  onValidationError={(error) => {
                    setEmailValidationError(error)
                    toast.error(error)
                  }}
                />
                {emailValidationError && (
                  <p className="text-xs text-destructive">{emailValidationError}</p>
                )}
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">
                    Email addresses that will receive notifications
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">How to use:</span> Type an email address and
                    press{' '}
                    <kbd className="px-1.5 py-0.5 text-xs font-semibold text-muted-foreground bg-muted border border-border rounded">
                      Enter
                    </kbd>{' '}
                    to add it. Click the × button on any badge to remove it.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Recipient phone numbers (optional)</Label>
                <TagInputMyCustomComp
                  value={recipientPhoneNumbers}
                  onChange={setRecipientPhoneNumbers}
                  placeholder="Type phone number and press Enter to add"
                />
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-muted-foreground">
                    Phone numbers for SMS notifications (include country code)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">How to use:</span> Type a phone number and press{' '}
                    <kbd className="px-1.5 py-0.5 text-xs font-semibold text-muted-foreground bg-muted border border-border rounded">
                      Enter
                    </kbd>{' '}
                    to add it. Click the × button on any badge to remove it.
                  </p>
                </div>
              </div>

              <Button
                className="w-full"
                disabled={
                  updateNotificationPreferencesMutation.isPending ||
                  notificationSlots.length === 0 ||
                  notificationMethods.length === 0 ||
                  ((notificationMethods.includes('email') ||
                    notificationMethods.includes('both')) &&
                    recipientEmails.length === 0) ||
                  ((notificationMethods.includes('sms') || notificationMethods.includes('both')) &&
                    recipientPhoneNumbers.length === 0) ||
                  emailValidationError !== null
                }
                onClick={() => {
                  if (notificationSlots.length === 0) {
                    toast.error('Please add at least one notification slot')
                    return
                  }

                  if (
                    notificationMethods.length === 0 ||
                    ((notificationMethods.includes('email') ||
                      notificationMethods.includes('both')) &&
                      recipientEmails.length === 0) ||
                    ((notificationMethods.includes('sms') ||
                      notificationMethods.includes('both')) &&
                      recipientPhoneNumbers.length === 0)
                  ) {
                    toast.error('Please fill in all required fields')
                    return
                  }

                  if (emailValidationError !== null) {
                    toast.error('Please fix email validation errors before saving')
                    return
                  }

                  // Validate all emails before saving
                  const invalidEmails = recipientEmails.filter(
                    (email) => validateEmail(email) !== null,
                  )
                  if (invalidEmails.length > 0) {
                    toast.error('Please remove invalid email addresses before saving')
                    return
                  }

                  updateNotificationPreferencesMutation.mutate({
                    clientId,
                    preferences: {
                      enabled: notificationEnabled,
                      notificationSlots,
                      advanceMinutes,
                      methods: notificationMethods,
                      recipientEmails: recipientEmails,
                      recipientPhoneNumbers: recipientPhoneNumbers,
                    },
                  })
                }}
              >
                {updateNotificationPreferencesMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save notification preferences
              </Button>

              <div className="rounded-md bg-muted p-3 text-xs">
                <p className="font-semibold mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>
                    Each slot checks for clock-ins within its time range and sends a notification
                    {advanceMinutes > 0 ? ` ${advanceMinutes} minutes before` : ' at'} the
                    notification time
                  </li>
                  <li>
                    Multiple slots allow checking at different times throughout the day (e.g., 9AM
                    checks 6AM-9AM, 1PM checks 9AM-1PM)
                  </li>
                  <li>Settings take effect immediately after saving</li>
                  <li>Notifications run automatically every day</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  )
}
